'use client';

import { useState, type ReactElement } from 'react';

import { useTranslations } from '@fshr/context/i18n-context';
import { useForm } from '@fshr/hooks';
import { cn } from '@fshr/shared-react';

import Alert from '../alert';
import Button from '../button';
import Checkbox from '../checkbox';
import Icon from '../icon';
import Input from '../input';
import TypeAhead from '../type-ahead';

import type {
	FindAReservationAnalyticsEvent,
	FindAReservationSubmitData,
	FoundReservationData,
	ResortOptionData,
} from './types';
import BevSafeText from '../bev-safe-text';

const COPY = {
	lookupFailed:
		'The information you entered does not match a current reservation in our system. Please check the information entered and try again. For assistance, please contact the Hotel or Resort directly.',
	acknowledgment:
		'I confirm that I am the guest and/or individual authorized and responsible for the requested reservation and payment details.',
	callUs: 'If you are having problems signing in, please call us.',
	tollFree: 'Other Toll-Free Numbers',
	lastName: 'Last Name',
	confirmationNumber: 'Confirmation Number',
	resortName: 'Resort Name',
	submit: 'Find Reservation',
	required: 'This field is required.',
	invalidOption: 'Please select a valid option.',
} as const;

const DEFAULT_SUPPORT_PHONE = '+1 (800) 819-5053';
const DEFAULT_SUPPORT_PHONE_HREF = 'tel:+18008195053';
const DEFAULT_TOLL_FREE_HREF = '/global-toll-free-telephone-numbers/';

type Props = {
	idPrefix: string;
	singleProperty?: { label: string; value: string };
	properties?: ResortOptionData[];
	disableSubmitUntilComplete: boolean;
	supportPhoneNumber?: string;
	supportPhoneHref?: string;
	tollFreeNumbersHref?: string;
	onSubmit: (values: FindAReservationSubmitData) => Promise<FoundReservationData | void>;
	onSuccess: (reservation?: FoundReservationData) => void;
	onAnalyticsEvent?: (event: FindAReservationAnalyticsEvent) => void;
};

export default function FindAReservationForm({
	idPrefix,
	singleProperty,
	properties,
	disableSubmitUntilComplete,
	supportPhoneNumber = DEFAULT_SUPPORT_PHONE,
	supportPhoneHref = DEFAULT_SUPPORT_PHONE_HREF,
	tollFreeNumbersHref = DEFAULT_TOLL_FREE_HREF,
	onSubmit,
	onSuccess,
	onAnalyticsEvent,
}: Props): ReactElement {
	const t = useTranslations();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasLookupError, setHasLookupError] = useState(false);

	const hasStaticResort = Boolean(singleProperty);

	const handleFormSubmit = async (values: Record<string, string>): Promise<void> => {
		setIsSubmitting(true);
		setHasLookupError(false);
		try {
			const reservation = await onSubmit({
				resortCode: values['resortCode'],
				lastName: values['lastName'],
				confirmationNumber: values['confirmationNumber'],
			});
			onAnalyticsEvent?.('form-submitted');
			onSuccess(reservation ?? undefined);
		} catch {
			setHasLookupError(true);
		} finally {
			setIsSubmitting(false);
		}
	};

	const { handleSubmit, registerField } = useForm({ onSubmit: handleFormSubmit });

	const resortField = registerField({
		name: 'resortCode',
		initialValue: singleProperty?.value ?? '',
		type: hasStaticResort ? 'default' : 'typeahead',
		options: {
			required: true,
			errorMessages: { empty: t(COPY.required), partial: t(COPY.invalidOption) },
		},
	});

	const lastNameField = registerField({
		name: 'lastName',
		initialValue: '',
		options: { required: true, errorMessages: { empty: t(COPY.required) } },
	});

	const confirmationNumberField = registerField({
		name: 'confirmationNumber',
		initialValue: '',
		options: { required: true, errorMessages: { empty: t(COPY.required) } },
	});

	const acknowledgmentField = registerField({
		name: 'acknowledgment',
		initialValue: '',
		type: 'checkbox',
		options: { required: true, errorMessages: { empty: t(COPY.required) } },
	});

	const isComplete = Boolean(
		resortField.value &&
			lastNameField.value &&
			confirmationNumberField.value &&
			acknowledgmentField.checked
	);

	const acknowledgmentId = `${idPrefix}-acknowledgment`;

	return (
		<form noValidate onSubmit={handleSubmit} className="flex w-full flex-col gap-200">
			{hasLookupError && (
				<Alert intent="error" kind="subtle" icon={<Icon icon="alert" />}>
					<span className="text-caption">{t(COPY.lookupFailed)}</span>
				</Alert>
			)}

			{hasStaticResort ? (
				<p className="text-center text-subtitle2">
					<BevSafeText>{singleProperty?.label}</BevSafeText>
				</p>
			) : (
				<TypeAhead
					{...resortField}
					id={`${idPrefix}-resort`}
					label={t(COPY.resortName)}
					placeholder={t(COPY.resortName)}
					noResults={t('No results')}
					options={properties ?? []}
				/>
			)}

			<Input
				{...lastNameField}
				id={`${idPrefix}-last-name`}
				label={t(COPY.lastName)}
				placeholder={t(COPY.lastName)}
				autoComplete="family-name"
			/>

			<Input
				{...confirmationNumberField}
				id={`${idPrefix}-confirmation-number`}
				label={t(COPY.confirmationNumber)}
				placeholder={t(COPY.confirmationNumber)}
				autoComplete="off"
			/>

			<Checkbox
				{...acknowledgmentField}
				id={acknowledgmentId}
				label={
					<label htmlFor={acknowledgmentId} className="text-body3">
						{t(COPY.acknowledgment)}
					</label>
				}
			/>

			<div className="flex justify-center">
				<Button
					type="submit"
					intent="filled"
					className={cn('min-w-[125px] px-300', {
						'border-button-primary-disabled bg-button-primary-disabled text-button-primary-disabled-inverse':
							disableSubmitUntilComplete && !isComplete,
					})}
					isLoading={isSubmitting}
					disabled={isSubmitting}
					data-incomplete={disableSubmitUntilComplete && !isComplete ? true : undefined}
				>
					{t(COPY.submit)}
				</Button>
			</div>

			{hasLookupError && (
				<div className="flex flex-col items-center gap-100 text-center">
					<p className="text-caption">{t(COPY.callUs)}</p>
					<p className="text-caption">
						<a className="underline" href={supportPhoneHref}>
							{supportPhoneNumber}
						</a>{' '}
						<a className="underline" href={tollFreeNumbersHref}>
							{t(COPY.tollFree)}
						</a>
					</p>
				</div>
			)}
		</form>
	);
}
